/**
 * useUserProfile Hook
 *
 * Fetches and manages current user's profile data from the WebAPI and
 * syncs the transformed result into userProfileSlice.
 */

import { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  selectProfile,
  selectIsProfileLoading,
  selectIsProfileFetching,
  selectProfileError,
  setProfile,
  setLoading,
  setFetching,
  setError,
} from '@/store/slices/userProfileSlice';
import { useGetClientProfileQuery } from '@/store/api/endpoints/client/userProfileApi';
import { clientProfileToUserProfileDetail } from '../utils/clientTransformer';
import type { UserProfileDetail } from '../utils/transformers';

// =============================================================================
// Types
// =============================================================================

export interface UseUserProfileResult {
  data: UserProfileDetail | null;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  refetch: () => void;
}

// =============================================================================
// Hook
// =============================================================================

export function useUserProfile(): UseUserProfileResult {
  const dispatch = useAppDispatch();
  const profile = useAppSelector(selectProfile);
  const isLoading = useAppSelector(selectIsProfileLoading);
  const isFetching = useAppSelector(selectIsProfileFetching);
  const error = useAppSelector(selectProfileError);

  const clientQuery = useGetClientProfileQuery();

  const queryIsLoading = clientQuery.isLoading;
  const queryIsFetching = clientQuery.isFetching;
  const queryError = clientQuery.error;
  const refetch = clientQuery.refetch;

  // ==========================================================================
  // Sync to slice
  // ==========================================================================

  useEffect(() => {
    dispatch(setLoading(queryIsLoading));
  }, [queryIsLoading, dispatch]);

  useEffect(() => {
    dispatch(setFetching(queryIsFetching));
  }, [queryIsFetching, dispatch]);

  useEffect(() => {
    if (clientQuery.data) {
      dispatch(setProfile(clientProfileToUserProfileDetail(clientQuery.data)));
    } else if (clientQuery.data === undefined && !clientQuery.isLoading && !clientQuery.isUninitialized) {
      dispatch(setProfile(null));
    }
  }, [clientQuery.data, clientQuery.isLoading, clientQuery.isUninitialized, dispatch]);

  useEffect(() => {
    if (queryError) {
      let errorMessage: string;
      if ('status' in queryError) {
        const typedError = queryError as { status: number };
        if (typedError.status === 404) {
          errorMessage = 'Profile not found.';
        } else if (typedError.status === 403) {
          errorMessage = 'Permission denied.';
        } else if (typedError.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `Error: ${typedError.status}`;
        }
      } else {
        errorMessage = 'An unexpected error occurred.';
      }
      dispatch(setError(errorMessage));
    } else if (!queryError && !queryIsLoading) {
      dispatch(setError(null));
    }
  }, [queryError, queryIsLoading, dispatch]);

  return {
    data: profile,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}
